import {CatalogItemSidebarDetailsStore, DetailsSecondariesStore} from '@design-board/stores'
import {SidebarTableRowStore} from '@modules/drawer/stores/SidebarTableRowStore'
import {DetailsSectionConfig, FieldType, ICatalogFieldConfig, ICreateItemData, IRollbackMutationData, OFFLINE_ID, ui, onSiteError, CatalogStore} from '@stores'
import {IEditableDetails} from '@stores/elasticsearch/mutations'
import {deepFreeze, differenceInObject, guid, recursivelyRemovePrivateFields} from '@utils'
import {IDataMapped} from 'centric-ui-components'
import {action, autorun, computed, observable, runInAction, toJS} from 'mobx'
import {_, deepdash, jsonComputedOptions, jsondiff, lodash_removePrivateFieldsCloneCustomizer, React, utils} from '@api'
import {createDraft, Draft, finishDraft} from 'immer'
import {CatalogInstanceChildStore} from '@stores/catalog/CatalogInstanceChildStore'
import {IDisposer} from 'mobx-utils'
import {deepObserve} from '@utils'

export const DEBUG_ITEM_DETAILS = DEBUG && false

/**
 * Fields not to diff on
 */
const ignoredDetailDiffFields = ['creationDate']
/*
  Fields to omit before making any changes to deeply observed object. (Solution to not to find any cycle while assigning article candidate)
*/
const deepObservePrototypeFields = new Set<string>(['__parent', '__path', 'ws'])

/**
 * Tracks data received from ES calls or offline data {@see OfflineItemWrapper} for changes and then applies those changes (saves them) to the backend or into local storage
 /**
 * YFTS-8803 Edit performance is poor + bugs
 *
 * Details data is shared between many different stores, all who need to act if the underlying data is mutated.

 This causes a number of issues in the app like data not reflecting always to everywhere expected like:

 YFTS-8800: Adding image on builder card is not reflecting same in open details paneIN PROGRESS ​- A change is made in builder card from drag and drop, card updates but sidebar doesn't

 YFTS-8712: Offline card_Delete image_The deleted images is not getting reflectedREADY FOR QA ​- Deleting an image in the sidebar not updating the builder car

 https://centric8.atlassian.net/browse/YFTS-7996 - Super slow performance when toggling LOD

 A significantly better solution is via something like immer which we are now exploring {@see CatalogItemDetailsStore.draft}

 ------

 For an Pending Item, which does not appear in any ES query result, we store our data offline in an indexed db via Dexie

 That data can be rendered in a variety of places and it all generally needs to stay in sync:
 * as cards in the main catalog
 * as cards in a group
 * as rows in a data table
 * in either sidebar (library items or sidebar details)
 * in context menus
 * in popovers (related offline data)
 * in the modal
 * in search results
 * in the pdf

 -- Sidebar Render of Pending Item
 * Sidebar store creates reference to us {@see SidebarDetailsStore.itemDetailsStore}
 *

 --- Pending Item Sync between Card and Sidebar (YFTS-8800 and YFTS-8712)
 For the card in the main catalog panel:
 1. We create a {@see CatalogItemsPanel} which internally creates a {@see CatalogItemsCardViewStore} which determines the Pending Items {@see CatalogItemsCardViewStore.offlineItems} to render
 (subject to things like filters, search text, sort order, grouping, workspace, etc {@see catalog.filteredOfflineItems}

 2. Each {@see OfflineItemWrapper} is loaded into an individual {@see BuilderCardWithPopoverStore} via the {@see BuilderCardWithPopover.props.item} prop

 Here's where things get confusing...
 - Each builder card has a related {@see CatalogItemDetailsStore} via {@see catalog.itemDetailStoreFor}
 - We copy the data handed to our builder card into originalData and data

 - We clone them into private copies via {@see BuilderCardWithPopoverStore.registerEffects} and {CatalogItemDetailsStore.replaceData}

 * Some places that data is currently _cloned
 *
 * - {@see CatalogItemDetailsStore.data}
 * - After (re)querying new data from the backend {@see catalog.refetch}
 * - After Applying a mutation {@see CatalogItemDetailsStore.applyMutations}
 *
 * *
 * {@see BuilderCardWithPopover}
 * {@see DataGridCell}
 * {@see SidebarDetailsStore}
 */
export class CatalogItemDetailsStore extends CatalogInstanceChildStore {
  constructor(public catalog: CatalogStore, public index: string, public isCreate: boolean, public details?: CatalogItemSidebarDetailsStore) {
    super(catalog)
  }

  private _dataChangeObserver : IDisposer
  dirtyPaths = observable.set<string>()

  registerEffects = () => {
    // console.log(`Mount CatalogItemDetailsStore`)
    this._toDispose.push(...[
      DEBUG_ITEM_DETAILS ? autorun(() => {
        const {isDirty, id, originalData, data} = this
        DEBUG_ITEM_DETAILS && originalData && console.log({id, isDirty, originalData, data})
      }) : null,
      !_.isEmpty(this.constrainedValues) && autorun(() => {
        const {isConstraintDirty, _assignConstrainedValues} = this
        _assignConstrainedValues(!isConstraintDirty)
      }, {name: `Assign constrained value on change`}),
      autorun(() => {
        const {_data} = this
        runInAction(() => {
          if (this._dataChangeObserver) {
            this._dataChangeObserver()
          }
          this._dataChangeObserver = deepObserve(_data, (change, path) => {
            const {dirtyPaths, originalData, id} = this

            const fullPath = `${path}${change['name'] ? `${path ? '.' : ''}${change['name']}` : ''}`

            DEBUG_ITEM_DETAILS && console.log(`Change observed`, {id, change, path, fullPath})
            if (_.has(change, 'newValue')) {
              const dirty = _.get(originalData, fullPath) !== change['newValue']
              dirty ? dirtyPaths.add(fullPath) : dirtyPaths.delete(fullPath)
            }
            else {
              dirtyPaths.add(fullPath)
            }
          })
        })
      }, {name: `Track dirty state for item details store ${this.id}`}),
      autorun(() => {
        const {isDirty, constrainedValues, isConstraintDirty, isApplyingMutations} = this
        if (!isApplyingMutations && isDirty && !_.isEmpty(constrainedValues)) {
          // TODO Will need to be reenabled for UR
          /*constrainedValues.forEach(async ({fromValue, fromValueOriginal, toValue, toValueOriginal, relatedProperty, from, to, field, esIndex, relatedField}) => {
            if (from !== `${field}.${to}` && field !== to && fromValue != toValue) {
              if (fromValue) {
                _.set(data, field, null)
              } else if (toValue) {
                // User set something like a colorway with no style set, we need to set the style to match

                const library = relatedField ? workspaces.libraryByIndex[relatedField.esIndex] : null

                if (library && relatedProperty) {
                  // Lookup the full row in the related library
                  const {items} = es.unwrapHitsResult(await es.search({
                    index: relatedField.esIndex,
                    body: {
                      query: {
                        match: {
                          [relatedProperty]: toValue
                        }
                      }
                    }
                  }))

                  if (items.length > 1) {
                    console.warn(`Multiple results found for ${esIndex}.${to} == ${toValue}`)
                  } else if (items.length == 0) {
                    onSiteError(new Error(`Unable to find ${esIndex}.${to} == ${toValue}`))
                  } else {
                    const {assignTo} = library
                    if (!assignTo) {
                      console.warn(`No assignTo property set on library ${library.name}`)
                    } else {
                      _.set(data, assignTo, _.first(items))
                    }
                  }
                } else {
                  if (relatedField) {
                    console.warn(`No library defined for related index ${relatedField.esIndex}`)
                  } else {
                    console.warn(`No field defined in ${catalog.workspaceConfig.title} that has a related esIndex for path ${from}`)
                  }
                }
              }
            }
          })*/
          this._assignConstrainedValues(!isConstraintDirty) //TODO - this is quick fix need to improve the logic to assign style and colorway from modal
          // console.log(toJS(constrainedValues))
        }
      }, {name: `Update constrained item detail value(s) if constraints are violated (style -> colorway for example)`})
    ].filter(_.identity))

    return this.dispose
  }

  _toDispose: IDisposer[] = []
  dispose = () => {
    // console.log(`Unmount CatalogItemDetailsStore`)
    this._toDispose.forEach(d => d())
  }

  @computed get isConstraintDirty() {
    const {constrainedFields, data} = this
    return constrainedFields.some(f => {
      const {constraint: {from, to}, field} = f,
        fromValue = _.get(data, from),
        toValue = _.get(data, `${field}.${to}`)
      return fromValue !== toValue && fromValue !== undefined && toValue !== undefined
    })
  }

  @action _assignConstrainedValues = (forceSkip: boolean = false) => {
    const {es, constrainedValues, data, catalog} = this,
      wasFrozen = this.isFrozen
    this.freeze()

    constrainedValues.forEach(async ({fromValue, fromValueOriginal, toValue, toValueOriginal, relatedProperty, from, to, field, esIndex, relatedField}) => {
      if (from !== `${field}.${to}` && field !== to && (fromValueOriginal != toValueOriginal || fromValue !== toValue)) {
        if (!forceSkip && fromValue) {
          _.set(data, field, null)
        } else if (toValue) {
          // User set something like a colorway with no style set, we need to set the style to match

          const library = relatedField ? this.workspaces.libraryByIndex[relatedField.esIndex] : null

          if (library && relatedProperty) {
            // Lookup the full row in the related library
            const {items} = es.unwrapHitsResult(await es.search({
              index: relatedField.esIndex,
              body: {
                query: {
                  match: {
                    [relatedProperty]: toValue
                  }
                }
              }
            }))

            if (items.length > 1) {
              console.warn(`Multiple results found for ${esIndex}.${to} == ${toValue}`)
            } else if (items.length == 0) {
              onSiteError(new Error(`Unable to find ${esIndex}.${to} == ${toValue}`))
            } else {
              const {assignTo} = library
              if (!assignTo) {
                console.warn(`No assignTo property set on library ${library.name}`)
              } else {
                _.set(data, assignTo, _.first(items))
              }
            }
          } else {
            if (relatedField) {
              console.warn(`No library defined for related index ${relatedField.esIndex}`)
            } else {
              console.warn(`No field defined in ${catalog.workspaceConfig.title} that has a related esIndex for path ${from}`)
            }
          }
        }
      }
    })
    !wasFrozen && this.unfreeze()
  }

  /**
   * To avoid making multiple mutation calls when changing a tag list, typing into an input, etc with auto-save turned on
   */
  @observable isFrozen = false
  @action freeze = () => this.isFrozen = true
  @action unfreeze = () => this.isFrozen = false

  @observable _data: Partial<IDataMapped> & Partial<ICreateItemData> & any
  @observable.ref originalData

  // @observable.ref draft: Draft<any>

  get data() { return this._data}

  /***
   * Do not make an action or autoruns won't track that we touched all fields
   *
   * This is called as our data can change from the outside by something like the user adding more view options - which will add / remove fields from our data source
   * YFTS-8800
   * @param dataToClone
   */
  @action replaceData = (dataToClone: any, keepOutstandingChanges = true) => {
    this.originalData && DEBUG_ITEM_DETAILS && console.trace(`replaceData - expensive double copy`, {d: this.id, dataToClone})

    const {outstandingChanges} = this
    this._data = _.cloneDeep(dataToClone)
    this.originalData = _.cloneDeep(this._data)

    if (keepOutstandingChanges) {
      _.mergeWith(this._data, outstandingChanges, utils.lodashMergeOverwriteArraysFn)
    }
    else {
      this.dirtyPaths.clear()
    }
  }

  @computed get outstandingChanges() {
    const {dirtyPaths, data} = this
    let r = {}

    dirtyPaths.forEach(v => _.set(r, v, _.get(data, v)))

    return r
  }

  @observable mutationData: IEditableDetails = {}
  @observable rollbackMutationData: IRollbackMutationData = {}

  @computed get constrainedFields(): Array<ICatalogFieldConfig & { relatedField?: ICatalogFieldConfig, relatedProperty?: string }> {
    const {index, catalog} = this
    if (index == catalog.index) {
      return catalog.catalogConfig.fields.filter(f => f.constraint).map(f => {
        const {relatedField, relatedProperty} = catalog.findConstraintRelatedField(f)

        return {...f, relatedField, relatedProperty}
      })
    }

    return []
  }

  @computed get constrainedValues() {
    const {data, constrainedFields, originalData} = this
    return constrainedFields.map(({relatedField, relatedProperty, field, esIndex, constraint: {from, to}}) => ({
        field,
        relatedField,
        relatedProperty,
        esIndex,
        from,
        to,
        fromValue: _.get(data, from),
        fromValueOriginal: _.get(originalData, from),
        toValue: _.get(data, `${field}.${to}`),
        toValueOriginal: _.get(originalData, `${field}.${to}`)
      }
    ))
  }

  // Did we make any mutations that were saved to the store?
  @observable wasChanged = false

  @observable isRefetchNeeded = false

  @computed get id() {
    const {data} = this
    return data?.exId ?? data?.id ?? data?._id
  }

  @observable isApplyingMutations = false

  @observable editableFieldText: { field: string, value, section?: DetailsSectionConfig } = null

  @action onBeginEditEditableField = (field: string, sectionId?: string, activeTabSection?: DetailsSecondariesStore | SidebarTableRowStore) => async (e: string & React.ChangeEvent<HTMLInputElement>) => {
    this.freeze()
    this.editableFieldText = {
      field,
      value: sectionId ? this.es.readField(activeTabSection?.dataById[sectionId], field).valAsString : _.get(this.data, field),
      section: {id: sectionId}
    }
  }

  @action onChangeEditableField = (val: string) => {
    const {editableFieldText} = this
    editableFieldText.value = val
  }

  @action onNumericValueChange = (field: string, section?: DetailsSectionConfig) => (val: number, valAsString: string) => {
    this.updateFieldData(field, val, section, valAsString)
  }

  @action onCancelEditableField = () => {
    this.unfreeze()
    this.editableFieldText = null
  }

  // @action onChangeFieldValue = (fieldObj: DetailsViewFieldConfig, fieldType: FieldType) =>
  @action onChangeFieldValue = (fieldObj: any, fieldType: FieldType, section?: DetailsSectionConfig, valueAsString?: string) =>
    async (e: string & React.ChangeEvent<HTMLInputElement>) => {
      const val = fieldObj.valueType == 'Float' ? parseFloat(e) : fieldObj.valueType == 'Int' ? parseInt(e) : e
      this.updateFieldData(fieldObj.field, fieldType === 'Boolean' || fieldType === 'headerCustomBoolean' ? e.target.checked : val, fieldObj.sectionId ?? section)
    }

  @action updateFieldData = (fieldPath: string, val: any, section?: DetailsSectionConfig, valAsString?: string) => {
    DEBUG_ITEM_DETAILS && console.log(`updateFieldData`, {fieldPath, val, valAsString, id: this.stores.storeId})
    const {catalog: {catalogConfig}} = this,
      dragType = catalogConfig?.dragType,
      field = dragType?.field,
      {_data, isCreate} = this

    if (_.isArray(val)) {
      val = val.map(item => {
        return !_.isArray(item) && _.isPlainObject(item) ? _.omitBy(item, (v, k) => deepObservePrototypeFields.has(k)) : item
      })
    }
    else if (_.isPlainObject(val)) {
      val = _.omitBy(val, (v, k) => deepObservePrototypeFields.has(k))
    }

    // handling case when field is not present
    // if (_.has(data, fieldPath) || isCreate || field == fieldPath) {
    if (_.has(_data, fieldPath) || isCreate || field == fieldPath || !_.has(_data, fieldPath)) {
      if (_.get(_data, fieldPath) != val) {
        _.set(_data, fieldPath, val)
      } else {
        this.editableFieldText = {field: fieldPath, section, value: valAsString ?? val}
        return
      }
    }
    this.onCancelEditableField()
  }

  //TODO: Track local images as per detail store and then make flag to avoid diff/isDirty
  @computed get isLocalImageDeleted() {
    const {originalData, stores} = this
    if (originalData?.images?.length == 0 || _.isUndefined(originalData?.images)) return false
    if (!stores?.sidebar?.right?.details) return false
    const filteredImages = stores.sidebar?.right?.details?.localImages?.filter((img: any) => {
      const imgSource = img?.__path ?? img?.source
      return imgSource == '' || imgSource == 'images' || imgSource == this?.catalog?.index
    }) ?? []
    return (originalData?.images?.length ?? 0) > filteredImages.length
  }

  @observable.ref mutationError: Error = null

  @computed get isDirty() {
    return this.dirtyPaths.size > 0
  }

  /**
   * Generate mutation object for the updated values of this.data
   * calls updateRemoteObject to save it
   * API takes care of saving it into ES or C8.
   * @private
   */
  @action applyMutations = async (allowRefetch = true) => {
    const {data, originalData, outstandingChanges, data: {_id: toId}, index, cancelMutations, catalog, catalog: {groupBy, filter}, stores: {sidebar, dragDrop}} = this,
      {settings: {autoSaveInlineChanges}, dialogs} = catalog,
      isOfflineItem = toId?.startsWith(OFFLINE_ID)

    let needsConfirm = false
    let diff = differenceInObject(data, originalData)

    DEBUG_ITEM_DETAILS && console.log(`Applying mutations for `, {id: toId, diff})
    try {
      if (isOfflineItem) {
        const {data} = this
        this.isApplyingMutations = true
        await catalog.upsertOfflineItem({index: index || catalog.index, value: data})
      } else {
        //console.log("diff::", diff)
        const {details} = this
        if (!_.isEmpty(diff)) {
          this.isApplyingMutations = true

          // TODO - below is wrong for complex cases like secondaries.  If we change a single secondary boolean we are sending up the entire secondaries array again instead of just the modification like below:
          /**
           * {
                "field": "secondaries",
                "value": {
                    "attributes": {
                        "asicsPreAdoption": false
                    }
                },
                "where": {
                    "attributes.VIP_ParentName": "ANA"
                },
                "fieldType": "arrXref"
            }
           */
          const mutations: any = _.keys(diff).reduce((acc, key) => {
              return {
                ...acc,
                [key]: {field: key, updatedValue: recursivelyRemovePrivateFields(_.cloneDeep(data[key]))}
              }
            }, {}),
            {createItem: {updateObjectInRemote}, refetch} = catalog
          if (details?.carouselStore?.activeImageOperation?.imageAction !== 'REORDER' && mutations?.images) {
            await sidebar?.right?.details?.uploadReorderImages(mutations?.images)
            delete mutations?.images
          }
          if (!_.isEmpty(mutations)) {
            const r = await updateObjectInRemote({
              toIndex: index,
              toId: catalog.idFor(data),
              changes: mutations,
              onCancel: cancelMutations,
              data: this.data
            })

            if (groupBy.hierarchyGroups.some(g => g && _.has(diff, g.groupName))) {
              await groupBy.viewStore.mgr.maybeRunQueryDebounced()

              // Rexpand to this object
              // Jarring - turned off
              // groupBy.hierarchyGroups.reduce((path, g) => {
              //   const groupValue = _.get(data, g.groupName)
              //
              //   g.setExpanded(stringify({ [g.groupName]: groupValue}), true, path)
              //   return [...path, groupValue]
              // }, [])
            }

            runInAction(async () => {
              if (r === false) {
                cancelMutations()
              }

              if (!r) {
                needsConfirm = true
                return
              }

              r?.data?.res?.message && dialogs.showSuccessMessage(r?.data?.res?.message)

              this.wasChanged = true
              if (allowRefetch && this.isRefetchNeeded) {
                refetch()
              }

              this.isRefetchNeeded = false

              const ddHandler = dragDrop.ddHandlers.get(catalog.idFor(data))
              if (ddHandler && !autoSaveInlineChanges) {
                dragDrop.applyOperation(ddHandler.def.id, 'success')
              }

              const localItem = catalog.settings.perItem[this.id]
              if (localItem?.unsavedChanges) {
                localItem.unsavedChanges = undefined
              }

              if (ddHandler && !autoSaveInlineChanges) {
                setTimeout(() => {
                  dragDrop.applyOperation(ddHandler.def.id, null)
                }, ui.settings.updateIndicatorMs)
              }
            })
          }
          // runInAction(() => {
          //   this.data = _.cloneDeep(data)
          //   this.originalData = _.cloneDeep(data)
          // })
        }

        if (details?.carouselStore?.activeImageOperation?.imageAction === 'REORDER') {
          await details.uploadReorderImages()
        }
      }

      //TODO - assignToField functionality pending - dragType work is pending
      //After assign from Drag show save button then assignToField is not
    } catch (err) {
      this.mutationError = err
      console.error(err)
    } finally {
      if (!needsConfirm) {
        runInAction(() => {
          if (this.isApplyingMutations) {
            this.isApplyingMutations = false
            this.replaceData(data, false)

            // Update the original builder card data if missmatched
            const {flexLayout, flexLayout: {previousTab, refetchAllCatalogRelatedTabs}} = this

            const itemDetailStores = previousTab?.catalogRoot?.catalog?.itemDetailStores
            let prevCardData = itemDetailStores ? itemDetailStores.get(data.id) : null

            if (prevCardData) {
              prevCardData._data = data
            }

            if (allowRefetch) {
              refetchAllCatalogRelatedTabs()
            }

            if ((!isOfflineItem || !diff?.images) && !this.stores.isInlineDetailsStore) {
              // Todo - perf - only refetch filters which would be impacted, ie that are in the mutations list
              filter.refetch(true)
              groupBy.queryGroupByCountsForMenu()
              catalog.es.aggregateDetailsRefetchId = guid()
              catalog.lastModifiedItem = {index, id: toId, data: {...this.originalData, secondaries: sidebar.right?.details?.secondariesStore ? [...sidebar.right.details.secondariesStore.secondaries] : []}}
            }
          }
        })
      }
    }

    // try {
    //   if (_.isEmpty(mutations)) {
    //     console.warn(`No mutations to apply - this function should not have been called`)
    //     this.mutationData = {}
    //     refetch()
    //     return
    //   }
    //   const {docMapping} = this.detailDef
    //   // console.log(toJS({toIndex, toId, idField, mutations, docMapping}, {'recurseEverything': true}))
    //   const to = {toId, toIndex, idField}
    //   // TODO- mutations[dragField] is not a right way to bifurcate,
    //   // what if we want tags-assignment + attribute-mutations to be applied in a single "apply"
    //   const {data: {res}, status} = !mutations[dragType?.field] ? await updateObjectInRemote({...to, mutations, docMapping})//await es.mutate({...to, mutations, docMapping})
    //                                                       : await es.assignToField({
    //       pathFromSource: dragType.path,
    //       entityType: dragType.entityType,
    //       unassign: false,
    //       from: {
    //         filter: {
    //           query: {
    //             terms: {
    //               _id: mutations[dragType.field].updatedValue
    //             }
    //           },
    //           _source: dragType.source
    //         }, index: dragType.index
    //       },
    //       to: {
    //         field: dragType.field,
    //         filter: {
    //           terms: {
    //             _id: [dataId]
    //           }
    //         }
    //         , index: toIndex
    //       },
    //       workspaceId: _id,
    //       isUpdated: true
    //     })
    //
    //   runInAction(`Mutations have been attempted`, () => {
    //     // console.log(res)
    //     if (status === 200) {
    //       const itemCount = Object.keys(mutations).length
    //       // update rollbackMutations, set values which are recent after this mutations
    //       _.forEach(this.mutationData.mutations, (mutableObj, key) => {
    //         this.rollbackMutationData[mutableObj.field] = _.get(this.data, mutableObj.field, null)
    //       })
    //       this.mutationData = {}
    //       // TODO : autoHideDuration on snackBar is not working with observable mobx flags
    //       setTimeout(() => {
    //         site._showSnackbar = false
    //         site._snackbarAlign = {vertical: 'bottom', horizontal: 'right'}
    //       }, ui.settings.AUTOHIDE_SNACKBAR_DURATION)
    //       refetch()
    //       showErrorMessage(`Updated ${itemCount} item${itemCount > 1 ? 's' : ''} in (${(this.data.merchProduct ? this.data.merchProduct.name : this.data.name) || catalog.catalogItemName})`, {vertical: 'bottom', horizontal: 'right'})
    //     } else {
    //       showErrorMessage(`Mutation Not Successful`)
    //       console.error('Mutation Not Successful !', {toIndex, toId, mutations})
    //     }
    //   })
    // } catch (error) {
    //   console.error(error)
    // }
    // finally {
    //   this.isApplyingMutations = false
    // }
  }

  @action undoDirtyChanges = () => {
    const {originalData, dirtyPaths, _data} = this
    dirtyPaths.forEach(v => {
      _.set(_data, v, _.get(originalData, v))
    })
    dirtyPaths.clear()
  }

  @action cancelMutations = () => {
    // if (this.isOffline) {

    this.undoDirtyChanges()

    const localItem = this.catalog.settings.perItem[this.id]
    if (localItem) {
      localItem.unsavedChanges = undefined
    }

    this.isApplyingMutations = false
    // }
    // else {
    //   // rollback to earlier data instance
    //   _.forEach(this.rollbackMutationData, (originalValue, field) => {
    //     this.updateFieldData(field, originalValue)
    //   })
    //   this.mutationData = {}
    // }
  }
}

export const cloneDeepNoPrivateFields = (dataToClone) => deepdash.filterDeep(dataToClone, lodash_removePrivateFieldsCloneCustomizer)



// WEBPACK FOOTER //
// ./src/modules/drawer/stores/CatalogItemDetailsStore.ts